import { useEffect, useState } from 'react';
import { PACE_TIERS } from '@ellie/shared';
import type { PaceCode } from '@ellie/shared';
import { supabase } from '@/lib/supabase';
import { Loader2, Save, ShoppingBag, AlertTriangle } from 'lucide-react';

interface ProductVisibility {
  hidden_pace_tiers: PaceCode[];
}

export function ProductsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [hiddenTiers, setHiddenTiers] = useState<PaceCode[]>([]);

  // Load current visibility settings on mount
  useEffect(() => {
    async function load() {
      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'product_visibility')
        .single();

      if (!fetchError && data?.value) {
        const val = data.value as ProductVisibility;
        if (Array.isArray(val.hidden_pace_tiers)) {
          setHiddenTiers(val.hidden_pace_tiers);
        }
      }

      setLoading(false);
    }

    load();
  }, []);

  function toggleTier(code: PaceCode) {
    setSaved(false);
    setHiddenTiers((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);

    const value: ProductVisibility = { hidden_pace_tiers: hiddenTiers };

    const { error: upsertError } = await supabase.from('app_settings').upsert(
      {
        key: 'product_visibility',
        value,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'key' },
    );

    if (upsertError) {
      setError(upsertError.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }

    setSaving(false);
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  const allHidden = hiddenTiers.length === PACE_TIERS.length;

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="mb-6 flex items-center gap-3">
        <ShoppingBag size={24} className="text-brand" />
        <h1 className="font-heading text-2xl text-text-primary">Products</h1>
      </div>

      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Store Visibility</h2>
        <p className="mb-5 text-sm text-text-muted">
          Toggle which pace tiers are visible in the store. Hidden tiers won't appear on the pricing
          page or in the order form.
        </p>

        {allHidden && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              All tiers are hidden. The store will show an empty pricing section and customers won't
              be able to place orders.
            </p>
          </div>
        )}

        <div className="space-y-3">
          {PACE_TIERS.map((tier) => {
            const visible = !hiddenTiers.includes(tier.code as PaceCode);
            return (
              <label
                key={tier.code}
                className="flex items-center gap-4 rounded-lg border border-border p-4 transition-colors hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={visible}
                  onChange={() => toggleTier(tier.code as PaceCode)}
                  className="h-4 w-4 rounded border-gray-300 text-brand accent-brand"
                />
                <div className="flex-1">
                  <p className="font-semibold text-text-primary">{tier.name}</p>
                  <p className="text-sm text-text-muted">{tier.description}</p>
                  <p className="text-xs text-text-light">~{tier.pages} pages</p>
                </div>
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    visible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {visible ? 'Visible' : 'Hidden'}
                </span>
              </label>
            );
          })}
        </div>

        {/* Save button + feedback */}
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving...' : 'Save'}
          </button>

          {saved && <span className="text-sm text-green-600">Visibility saved.</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </section>
    </div>
  );
}
